import { AnimatePresence } from 'framer-motion';
import { Globe2, MapPin } from 'lucide-react';
import { TourStyleCard } from '../ui/TourStyleCard';
import { WEATHER_BTN_DISMISS, WEATHER_BTN_SUBSCRIBE } from '../weather/weatherDesign';
import type { NavigationRegionBlock } from '../../utils/regionAvailability';

export type NavigationRegionModalVariant = 'start_location' | 'live_navigation' | 'navigation_block';

interface NavigationRegionModalProps {
  open: boolean;
  onClose: () => void;
  variant?: NavigationRegionModalVariant;
  block?: NavigationRegionBlock | null;
  onSelectInGhana?: () => void;
}

const START_LOCATION_COPY = {
  headerLabel: 'Directions',
  title: 'Coordal Maps is currently available in Ghana.',
  body: 'Live navigation requires a start location in Ghana. Select a location in Ghana to continue.',
} as const;

const LIVE_NAVIGATION_COPY = {
  headerLabel: 'Live navigation',
  title: 'Coordal Maps is currently available in Ghana.',
  body: 'Live navigation requires location access and your current position to be in Ghana. Enable location services and try again when you are in Ghana.',
} as const;

function resolveCopy(
  variant: NavigationRegionModalVariant,
  block: NavigationRegionBlock | null | undefined,
): {
  headerLabel: string;
  title: string;
  body: string;
  icon: 'globe' | 'pin';
} {
  if (variant === 'start_location') {
    return { ...START_LOCATION_COPY, icon: 'globe' };
  }

  if (variant === 'live_navigation') {
    return { ...LIVE_NAVIGATION_COPY, icon: 'globe' };
  }

  if (!block) {
    return {
      headerLabel: 'Navigation',
      title: 'Coordal Maps is currently available in Ghana.',
      body: 'Pan to Ghana to explore the map, get directions, and use transport.',
      icon: 'globe',
    };
  }

  switch (block.reason) {
    case 'origin':
      return {
        headerLabel: 'Turn-by-turn navigation',
        title: START_LOCATION_COPY.title,
        body: START_LOCATION_COPY.body,
        icon: 'globe',
      };
    case 'destination':
      return {
        headerLabel: 'Turn-by-turn navigation',
        title: 'This destination is outside Ghana',
        body: block.destinationName
          ? `“${block.destinationName}” is outside Ghana. Coordal navigation supports routes within Ghana. Choose a destination in Ghana to continue.`
          : 'This destination is outside Ghana. Coordal navigation supports routes within Ghana. Choose a destination in Ghana to continue.',
        icon: 'pin',
      };
    case 'both':
      return {
        headerLabel: 'Turn-by-turn navigation',
        title: START_LOCATION_COPY.title,
        body: START_LOCATION_COPY.body,
        icon: 'globe',
      };
  }
}

/** Ghana service-area messaging — product-tour card styling. */
export function NavigationRegionModal({
  open,
  onClose,
  variant = 'navigation_block',
  block = null,
  onSelectInGhana,
}: NavigationRegionModalProps) {
  const copy = resolveCopy(variant, block);
  const showStartLocationActions = variant === 'start_location' && onSelectInGhana;

  return (
    <AnimatePresence>
      {open && (variant === 'start_location' || variant === 'live_navigation' || block) && (
        <div
          className="pointer-events-auto fixed inset-0 z-[110] flex items-end justify-center bg-black/40 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] dark:bg-black/55 sm:items-center sm:p-4"
          role="presentation"
          onClick={onClose}
        >
          <div
            className="w-full max-w-md"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <TourStyleCard
              embedded
              headerIcon={
                copy.icon === 'pin' ? (
                  <MapPin size={16} className="shrink-0 text-white" aria-hidden />
                ) : (
                  <Globe2 size={16} className="shrink-0 text-white" aria-hidden />
                )
              }
              headerLabel={copy.headerLabel}
              title={copy.title}
              onDismiss={onClose}
              dismissLabel="Close"
              footer={
                showStartLocationActions ? (
                  <div className="mt-4 space-y-2">
                    <button
                      type="button"
                      onClick={onSelectInGhana}
                      className={WEATHER_BTN_SUBSCRIBE}
                    >
                      Select location in Ghana
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className={WEATHER_BTN_DISMISS}
                    >
                      Close
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={onClose}
                    className={`${WEATHER_BTN_DISMISS} mt-4`}
                  >
                    Back to directions
                  </button>
                )
              }
            >
              <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                {copy.body}
              </p>
            </TourStyleCard>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}
